import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { ALLOWED_STEP_ID, STEP_ID, StepId } from '@/types'
import { DeviceError } from '@/types/device.ts'
import { isTokenValid } from '@/util'
import { createGlobalState } from '@vueuse/core'
import {
  DeepIdError,
  GatewayError,
  IDENTIFICATION_HANDLERS_NEXT_TASK_ID,
} from '@deepcloud/api-deep-id'
import type {
  FlowData,
  IdentStep,
  PersistedState,
  StepData,
} from '@/types/ident'

export const IDENT_FLOW_STATE_KEY = 'ident_flow_state'

export const useIdentFlowState = createGlobalState(() => {
  const route = useRoute()

  const flowState = useSessionStorage<PersistedState | null>(
    IDENT_FLOW_STATE_KEY,
    null,
    {
      serializer: {
        read: (v) => {
          try {
            return v ? (JSON.parse(v) as PersistedState) : null
          } catch {
            return null
          }
        },
        write: (v) => JSON.stringify(v),
      },
    },
  )

  const logoIsVisible = ref(true)
  const error = ref<string | undefined>()
  const errorData = ref<DeepIdError | GatewayError | undefined>()

  const errorDevice = ref<DeviceError | undefined>()
  const errorDeviceData = ref<Error | unknown | undefined>()

  const isResolving = ref(false)
  const isEntering = ref(false)

  // ── Derived ──────────────────────────────────────────────────────────────

  const currentId = computed<StepId>(() => route.params.stepId as StepId)
  const data = computed(() => flowState.value?.data ?? {})
  // step the stored `data` belongs to
  const dataStepId = computed(() => flowState.value?.currentId)
  const flowData = computed(() => flowState.value?.flowData ?? ({} as FlowData))
  const hasState = computed(() => flowState.value !== null)
  const isWebReEnrollment = computed(
    () =>
      flowData.value.caseStatus?.nextTask?.id ===
      IDENTIFICATION_HANDLERS_NEXT_TASK_ID.ID_WEB_RE_ENROLLMENT,
  )

  // after a ReIdent Light web re-enrollment the backend only accepts the token from the ReEnrollment
  function getIdentToken(): string | undefined {
    return isTokenValid(
      flowData.value.webReEnrollment?.weakIdToken,
      flowData.value.webReEnrollment?.weakExpiration,
    )
      ? flowData.value.webReEnrollment?.weakIdToken
      : flowData.value.identification?.idToken
  }

  // ── Initialisation ───────────────────────────────────────────────────────

  function initFromSteps(identSteps: IdentStep[]) {
    if (currentId.value && currentId.value !== STEP_ID.HANDOVER_CONTINUE) return // already initialized

    let savedId: StepId | undefined

    // if a token is available, we come from a device handover
    if (flowData.value.token) {
      savedId = STEP_ID.HANDOVER_CONTINUE
    } else {
      savedId = flowState.value?.currentId
    }

    const firstId = identSteps[0]?.id
    const validId =
      savedId && identSteps.some((s) => s.id === savedId) ? savedId : firstId

    if (!validId) {
      clearState()
      return
    }

    const storedData =
      flowState.value?.currentId === validId ? flowState.value.data : undefined

    flowState.value = {
      currentId: validId,
      data:
        storedData ??
        (identSteps.find((step) => step.id === validId)?.props || {}),
      flowData: flowState.value?.flowData ?? {},
    }
  }

  function defaultCanVisit(stepId: StepId): boolean {
    if (ALLOWED_STEP_ID.has(stepId)) return true
    return !!flowData.value.identificationCase?.identificationCaseUid
  }

  // ── Mutations ────────────────────────────────────────────────────────────

  function replaceData(payload: StepData, stepId?: StepId) {
    flowState.value = {
      ...flowState.value!,
      ...(stepId && { currentId: stepId }),
      data: payload,
    }
  }

  function mergeFlowData(payload: Partial<FlowData>) {
    flowState.value = {
      ...flowState.value!,
      flowData: {
        ...flowState.value?.flowData,
        ...(payload as Record<string, unknown>),
      },
    }
  }

  function replaceState(payload: PersistedState | undefined) {
    flowState.value = {
      ...flowState.value!,
      ...(payload || {}),
    }
  }

  function clearState() {
    flowState.value = null
    error.value = undefined
    errorData.value = undefined
    errorDevice.value = undefined
    errorDeviceData.value = undefined
  }

  function resetState(pipeline: IdentStep[]) {
    clearState()
    initFromSteps(pipeline)
  }

  return {
    // Derived readable state
    currentId,
    hasState,
    data,
    flowData,
    isWebReEnrollment,
    logoIsVisible,
    error,
    errorData,

    errorDevice,
    errorDeviceData,

    isResolving,
    isEntering,

    defaultCanVisit,
    getIdentToken,

    // Actions
    initFromSteps,
    replaceData,
    dataStepId,
    mergeFlowData,
    replaceState,
    clearState,
    resetState,
  }
})
